"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Heart, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
gsap.registerPlugin(useGSAP, ScrollTrigger);
function JoinForm() {
  const [form, setForm] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fetchTime, setFetchTime] = useState<number>(0);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [newsletter, setNewsletter] = useState(true);
  const [hpValue, setHpValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    code: string;
    message: string;
  }>>({});
  const successMessage = "Request sent! A pro will contact you shortly.";
  const loadFormDefinition = () => {
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setLoadError("error");
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/homeowner_service_request").then((data: any) => {
      setForm(data);
      setFetchTime(Date.now());
    }).catch((err: any) => {
      if (err?.status === 404) {
        client.pluginRequest("wvc-forms/v1/forms").then((res: any) => {
          const items = res?.items ?? [];
          const matched = items.find((f: any) => f.key === "homeowner_service_request") || (items.length === 1 ? items[0] : null) || items.find((f: any) => /homeowner|service|quote|booking/i.test(f.name || ""));
          if (matched?.key) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`).then((defData: any) => {
              setForm(defData);
              setFetchTime(Date.now());
            });
          } else {
            setLoadError("missing");
          }
        }).catch(() => {
          setLoadError("missing");
        });
      } else {
        setLoadError("error");
      }
    });
  };
  useEffect(() => {
    loadFormDefinition();
  }, []);
  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPhone("");
    setNewsletter(true);
    setHpValue("");
    setIsSubmitted(false);
    setSubmitError(null);
    setFieldErrors({});
    setClientErrors({});
    loadFormDefinition();
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const newErrors: Record<string, string> = {};
    if (!fullName.trim()) {
      newErrors["full_name"] = "Please enter your name";
    } else if (fullName.trim().length < 2) {
      newErrors["full_name"] = "At least 2 characters";
    }
    if (!email.trim()) {
      newErrors["email"] = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors["email"] = "Please enter a valid email";
    }
    if (phone.trim() && !/^[+]?[\d\s().-]{7,}$/.test(phone.trim())) {
      newErrors["phone"] = "Please enter a valid phone number";
    }
    if (Object.keys(newErrors).length > 0) {
      setClientErrors(newErrors);
      return;
    }
    setClientErrors({});
    setFieldErrors({});
    setSubmitError(null);
    setIsSubmitting(true);
    const client = getWvcClient();
    if (!client?.pluginRequest || !form) {
      setIsSubmitting(false);
      setSubmitError("Submission failed. Please try again.");
      return;
    }
    const minSeconds = form?.spam?.min_seconds ?? 3;
    const elapsed = (Date.now() - fetchTime) / 1000;
    if (elapsed < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, Math.ceil((minSeconds - elapsed) * 1000) + 100));
    }
    const values: Record<string, any> = {};
    if (Array.isArray(form.fields)) {
      for (const field of form.fields) {
        if (["heading", "paragraph", "total", "payment"].includes(field.type)) continue;
        const k = field.key;
        if (k === "full_name" || k === "name") {
          values[field.id] = fullName.trim();
        } else if (k === "email") {
          values[field.id] = email.trim();
        } else if (k === "phone") {
          values[field.id] = phone.trim();
        } else if (k === "service_type") {
          values[field.id] = "";
        } else if (k === "service_address") {
          values[field.id] = "";
        } else if (k === "preferred_date") {
          values[field.id] = "";
        } else if (k === "message") {
          values[field.id] = "";
        } else if (k === "appliance_type") {
          values[field.id] = "";
        } else if (k === "source_page") {
          values[field.id] = "Services";
        } else if (k === "source_city") {
          values[field.id] = "";
        } else if (k === "source_service") {
          values[field.id] = "";
        } else if (k === "newsletter") {
          values[field.id] = newsletter ? "1" : "";
        } else if (field.default !== undefined && field.default !== null) {
          values[field.id] = field.default;
        }
      }
    }
    const payload: any = {
      values,
      token: form.spam?.token,
      hp: form.spam?.honeypot?.field ? {
        [form.spam.honeypot.field]: hpValue
      } : undefined,
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    const formKey = form.key || "homeowner_service_request";
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formKey}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        setIsSubmitted(true);
      } else if (res?.code === "frms_invalid_token") {
        const fresh = await client.pluginRequest(`wvc-forms/v1/forms/${formKey}`);
        setForm(fresh);
        const retryMin = fresh?.spam?.min_seconds ?? 3;
        await new Promise(resolve => setTimeout(resolve, retryMin * 1000 + 100));
        payload.token = fresh.spam?.token;
        const retryRes = await client.pluginRequest(`wvc-forms/v1/forms/${formKey}/submit`, {
          method: "POST",
          data: payload
        });
        if (retryRes?.success) {
          setIsSubmitted(true);
        } else if (retryRes?.code === "frms_validation_failed") {
          setFieldErrors(retryRes.errors ?? {});
        } else {
          setSubmitError(retryRes?.message || "Submission failed. Please try again.");
        }
      } else if (res?.code === "frms_validation_failed") {
        setFieldErrors(res.errors ?? {});
      } else {
        setSubmitError(res?.message || "Submission failed. Please try again.");
      }
    } catch {
      setSubmitError("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg bg-card p-6 text-card-foreground">
        <p className="font-default text-base font-medium">{successMessage}</p>
        <Button type="button" variant="outline" onClick={resetForm} className="mt-4 rounded-lg border-primary/40 text-foreground hover:bg-primary/10">
          {"Send another"}
        </Button>
      </div>;
  }
  if (loadError) {
    return <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3">
        <p className="text-sm text-destructive-foreground">
          {"Unable to load the request form. Please try again later."}
        </p>
      </div>;
  }
  const fullNameField = form?.fields?.find((f: any) => f.key === "full_name" || f.key === "name");
  const emailField = form?.fields?.find((f: any) => f.key === "email");
  const phoneField = form?.fields?.find((f: any) => f.key === "phone");
  const nameError = clientErrors["full_name"] || fullNameField && fieldErrors[fullNameField.id]?.message || fieldErrors["full_name"]?.message;
  const emailError = clientErrors["email"] || emailField && fieldErrors[emailField.id]?.message || fieldErrors["email"]?.message;
  const phoneError = clientErrors["phone"] || phoneField && fieldErrors[phoneField.id]?.message || fieldErrors["phone"]?.message;
  return <form onSubmit={handleSubmit} className="space-y-5">
      {form?.spam?.honeypot?.field && <input type="text" name={form.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} style={{
      position: "absolute",
      left: "-9999px"
    }} tabIndex={-1} autoComplete="off" aria-hidden="true" />}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="grid gap-2">
          <label htmlFor="req-name" className="sr-only">{"Full Name"}</label>
          <Input id="req-name" type="text" placeholder="John Smith" value={fullName} onChange={e => {
          setFullName(e.target.value);
          if (nameError) {
            setClientErrors(prev => {
              const updated = {
                ...prev
              };
              delete updated["full_name"];
              return updated;
            });
            if (fullNameField) {
              setFieldErrors(prev => {
                const updated = {
                  ...prev
                };
                delete updated[fullNameField.id];
                return updated;
              });
            }
          }
        }} className="h-12 rounded-lg border-transparent bg-muted px-5 py-3 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/30" />
          {nameError && <p className="text-sm text-destructive">{nameError}</p>}
        </div>

        <div className="grid gap-2">
          <label htmlFor="req-email" className="sr-only">{"Email Address"}</label>
          <Input id="req-email" type="email" placeholder="email@example.com" value={email} onChange={e => {
          setEmail(e.target.value);
          if (emailError) {
            setClientErrors(prev => {
              const updated = {
                ...prev
              };
              delete updated["email"];
              return updated;
            });
            if (emailField) {
              setFieldErrors(prev => {
                const updated = {
                  ...prev
                };
                delete updated[emailField.id];
                return updated;
              });
            }
          }
        }} className="h-12 rounded-lg border-transparent bg-muted px-5 py-3 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/30" />
          {emailError && <p className="text-sm text-destructive">{emailError}</p>}
        </div>
      </div>

      <div className="grid gap-2">
        <label htmlFor="req-phone" className="sr-only">{"Phone Number"}</label>
        <Input id="req-phone" type="tel" placeholder="(555) 000-0000 (optional)" value={phone} onChange={e => {
        setPhone(e.target.value);
        if (phoneError) {
          setClientErrors(prev => {
            const updated = {
              ...prev
            };
            delete updated["phone"];
            return updated;
          });
          if (phoneField) {
            setFieldErrors(prev => {
              const updated = {
                ...prev
              };
              delete updated[phoneField.id];
              return updated;
            });
          }
        }
      }} className="h-12 rounded-lg border-transparent bg-muted px-5 py-3 text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/30" />
        {phoneError && <p className="text-sm text-destructive">{phoneError}</p>}
      </div>

      <div className="flex flex-row items-start gap-3 space-y-0">
        <Checkbox id="req-newsletter" checked={newsletter} onCheckedChange={checked => setNewsletter(!!checked)} className="mt-1 border-muted-foreground/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
        <div className="grid gap-1 leading-none">
          <label htmlFor="req-newsletter" className="cursor-pointer text-sm font-medium text-foreground">
            {"Get service updates"}
          </label>
          <p className="text-xs text-muted-foreground">
            {"Receive seasonal maintenance tips and offers."}
          </p>
        </div>
      </div>

      {submitError && <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button type="submit" disabled={isSubmitting || !form} size="lg" className="h-12 w-full rounded-lg bg-primary px-8 text-base font-semibold text-primary-foreground shadow hover:bg-primary/90 sm:w-auto">
          {isSubmitting ? "Sending..." : "Request a quote"}
        </Button>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Heart className="size-4 text-primary" aria-hidden="true" />
          {"We respect your privacy."}
        </p>
      </div>
    </form>;
}
export default function Quoterequestcta() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".jdc-text", {
        y: 24
      });
      gsap.to(".jdc-text", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.09,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".jdc-collage", {
        y: 30
      });
      gsap.to(".jdc-collage", {
        opacity: 1,
        y: 0,
        duration: 1,
        stagger: 0.12,
        delay: 0.3,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.to(".jdc-doodle", {
        y: 4,
        rotate: 2,
        duration: 6,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1
      });
      gsap.to(".jdc-shape", {
        y: -20,
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".jdc-text", ".jdc-collage"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="34213" id="quoterequestcta" className="bg-background text-foreground relative overflow-hidden py-28 md:py-40">
      <div ref={container} className="mx-auto max-w-[1240px] px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
          <div className="relative lg:col-span-7">
            <svg className="jdc-doodle absolute -left-2 -top-8 size-14 md:-left-6 md:-top-10 md:size-20" viewBox="0 0 80 80" fill="none" stroke="var(--chart-2)" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M10 20 L18 12" />
              <path d="M28 8 L32 18" />
              <path d="M46 6 L44 16" />
              <path d="M60 14 L54 22" />
              <path d="M14 34 L22 30" />
              <path d="M40 30 L36 40" />
            </svg>

            <p className="jdc-text text-xs font-semibold uppercase tracking-[0.14em] text-primary" style={{
            opacity: 0
          }}>
              {"Request a quote"}
            </p>

            <h2 className="jdc-text mt-5 font-default text-4xl font-semibold leading-[1.02] tracking-tight text-foreground md:text-5xl lg:text-6xl" style={{
            opacity: 0
          }}>
              {"Find "}
              <span className="relative inline-block">
                {"local"}
                <svg className="absolute -bottom-2 left-0 h-3 w-full" viewBox="0 0 120 12" fill="none" stroke="var(--chart-3)" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <path d="M2 8 Q 30 2, 60 6 T 118 5" />
                </svg>
              </span>
              {" pros for your home."}
            </h2>

            <p className="jdc-text mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg" style={{
            opacity: 0
          }}>
              {"Connect with independent local pros for appliance repair, plumbing, HVAC diagnostics, and electrical services. Request a match to get quotes directly from trade specialists in your area. You can also call us directly at "}
              <a href="tel:+14708479076" className="font-semibold text-foreground underline decoration-primary underline-offset-4 hover:text-primary">
                {"(470) 847-9076"}
              </a>
              {"."}
            </p>

            <div className="jdc-text mt-10" style={{
            opacity: 0
          }}>
              <JoinForm />
            </div>
          </div>

          <div className="relative min-h-[420px] lg:col-span-5 lg:min-h-[520px]">
            <div className="jdc-shape jdc-collage absolute right-6 top-4 h-72 w-72 md:h-80 md:w-80" style={{
            backgroundColor: "var(--primary)",
            opacity: 0.22,
            borderRadius: "62% 38% 55% 45% / 48% 55% 45% 52%"
          }} aria-hidden="true" />

            <div className="jdc-collage absolute left-2 top-16 w-64 rotate-[-4deg] rounded-lg bg-card p-5 text-card-foreground shadow-xl md:left-0 md:top-20 md:w-72" style={{
            opacity: 0
          }}>
              <div className="flex items-center gap-2 border-b border-border/60 pb-3">
                <div className="flex size-8 items-center justify-center rounded-lg bg-primary/15">
                  <Mail className="size-4 text-primary" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] font-medium text-muted-foreground">{"Handy Heroes Atlanta"}</p>
                  <p className="text-xs font-semibold text-card-foreground">{"Service Request Match"}</p>
                </div>
              </div>
              <p className="mt-3 text-[13px] font-semibold text-card-foreground">
                {"Service request received"}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {"Your request has been matched with independent local pros serving your area."}
              </p>
              <div className="mt-3 flex gap-2">
                <span className="rounded-lg bg-chart-3/30 px-2 py-0.5 text-[10px] font-semibold text-foreground">
                  {"MATCHED"}
                </span>
                <span className="rounded-lg bg-primary/20 px-2 py-0.5 text-[10px] font-semibold text-primary">
                  {"Local Pro"}
                </span>
              </div>
            </div>

            <div className="jdc-collage absolute right-2 top-40 w-64 rotate-[3deg] rounded-lg bg-card p-5 text-card-foreground shadow-xl md:right-0 md:top-48 md:w-72" style={{
            opacity: 0
          }}>
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                  {"Quote request sent"}
                </p>
                <span className="text-[10px] text-muted-foreground">{"#HH-Atlanta"}</span>
              </div>
              <p className="mt-2 text-sm font-semibold text-card-foreground">
                {"Local pro will reach out"}
              </p>
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{"Service Category"}</span>
                  <span className="font-medium text-card-foreground">{"HVAC & Cooling"}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{"Coverage Area"}</span>
                  <span className="font-medium text-card-foreground">{"Metro Atlanta"}</span>
                </div>
                <div className="flex items-center justify-between border-t border-border/60 pt-2 text-xs">
                  <span className="font-semibold text-card-foreground">{"Estimate"}</span>
                  <span className="font-semibold text-card-foreground">{"Direct from Pro"}</span>
                </div>
              </div>

              <div className="absolute -bottom-6 -right-4 flex size-14 items-center justify-center rounded-lg bg-background shadow-lg" aria-hidden="true">
                <Mail className="size-5 text-primary" />
              </div>
            </div>

            <svg className="jdc-doodle absolute right-0 top-0 size-14 md:size-16" viewBox="0 0 60 60" fill="none" stroke="var(--chart-3)" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M30 6 L33 26 L52 30 L33 34 L30 54 L27 34 L8 30 L27 26 Z" />
            </svg>

            <Sparkles className="jdc-doodle absolute bottom-4 left-8 size-8 text-primary md:bottom-8" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>;
}